/**
 * AgendaSummary — Disponibilidad semanal del terapeuta.
 * Muestra los próximos 7 días con los bloques de horario disponibles.
 * Al hacer clic en un día con disponibilidad se abre un bottom-sheet con los slots.
 * Los datos vienen de user.current.extraData.timeAvailability (campo JSON en DB).
 * Diseño: Isabel Moreno (UX/UI Lead) · Implementación: Sofía García (Frontend)
 */
import React, { useMemo, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useHistory } from 'react-router-dom';
import useUser from 'state/user';
import {
    BRAND_GREEN_PRIMARY,
    DARKER_TEXT,
} from 'resources/constants/colors';
import { format, addDays, startOfDay } from 'date-fns';
import { es } from 'date-fns/locale';

// ── Helper ────────────────────────────────────────────────────────────────────

/**
 * Agrupa los slots de timeAvailability por día (yyyy-MM-dd).
 * timeAvailability = { key: ISODateString, ... }
 */
function groupSlotsByDay(timeAvailability = {}) {
    const map = {};
    if (!timeAvailability) return map;
    Object.values(timeAvailability).forEach(iso => {
        const d = new Date(iso);
        if (isNaN(d)) return;
        const dayKey = format(startOfDay(d), 'yyyy-MM-dd');
        if (!map[dayKey]) map[dayKey] = [];
        map[dayKey].push(d);
    });
    Object.values(map).forEach(slots => slots.sort((a, b) => a - b));
    return map;
}

// ── Animaciones ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const slideUp = keyframes`
    from { opacity: 0; transform: translateY(40px); }
    to   { opacity: 1; transform: translateY(0); }
`;

// ── Estilos — Semana ──────────────────────────────────────────────────────────

const Section = styled.div`
    animation: ${fadeUp} 0.35s 0.05s ease both;
`;

const SectionLabel = styled.p`
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: #aaa;
    margin: 0 0 8px;
    padding: 0 2px;
`;

const Card = styled.div`
    background: #fff;
    border-radius: 16px;
    padding: 14px 16px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.06);
`;

const WeekRow = styled.div`
    display: flex;
    gap: 6px;
    justify-content: space-between;
`;

const DayBubble = styled.button`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 8px 4px;
    border-radius: 12px;
    border: none;
    cursor: ${({ hasSlots }) => hasSlots ? 'pointer' : 'default'};
    background: ${({ isToday, hasSlots, isSelected }) =>
        isToday && hasSlots ? BRAND_GREEN_PRIMARY :
        isSelected ? BRAND_GREEN_PRIMARY + '20' :
        hasSlots ? BRAND_GREEN_PRIMARY + '12' : '#f7f7f7'};
    outline: ${({ isSelected, isToday }) =>
        isSelected && !isToday ? '2px solid ' + BRAND_GREEN_PRIMARY : 'none'};
    outline-offset: 1px;
    transition: opacity 0.15s, background 0.15s;
    -webkit-tap-highlight-color: transparent;
    &:active { opacity: ${({ hasSlots }) => hasSlots ? 0.7 : 1}; }
`;

const DayLabel = styled.span`
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    color: ${({ isToday, hasSlots }) =>
        isToday && hasSlots ? 'rgba(255,255,255,0.85)' : '#999'};
`;

const DayNum = styled.span`
    font-size: 15px;
    font-weight: 800;
    color: ${({ isToday, hasSlots }) =>
        isToday && hasSlots ? '#fff' :
        hasSlots ? BRAND_GREEN_PRIMARY : '#ccc'};
`;

const SlotBadge = styled.span`
    font-size: 9px;
    font-weight: 700;
    min-height: 8px;
    color: ${({ isToday }) => isToday ? 'rgba(255,255,255,0.9)' : BRAND_GREEN_PRIMARY};
`;

const EditHint = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 10px;
    padding: 7px 12px;
    border-radius: 20px;
    border: 1px dashed ${BRAND_GREEN_PRIMARY}60;
    background: transparent;
    color: ${BRAND_GREEN_PRIMARY};
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    width: 100%;
    justify-content: center;
    &:active { opacity: 0.7; }
`;

// ── Estilos — Modal ───────────────────────────────────────────────────────────

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.45);
    z-index: 1000;
    display: flex;
    align-items: flex-end;
    justify-content: center;
`;

const ModalSheet = styled.div`
    width: 100%;
    max-width: 520px;
    background: #fff;
    border-radius: 24px 24px 0 0;
    padding: 0 0 env(safe-area-inset-bottom, 16px);
    animation: ${slideUp} 0.28s cubic-bezier(0.22,1,0.36,1) both;
    max-height: 70vh;
    display: flex;
    flex-direction: column;
`;

const ModalHandle = styled.div`
    width: 36px;
    height: 4px;
    border-radius: 2px;
    background: #e0e0e0;
    margin: 12px auto 0;
    flex-shrink: 0;
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px 12px;
    flex-shrink: 0;
`;

const ModalTitle = styled.h3`
    font-size: 17px;
    font-weight: 800;
    color: ${DARKER_TEXT};
    margin: 0;
    text-transform: capitalize;
`;

const CloseBtn = styled.button`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: #f2f2f2;
    color: #888;
    font-size: 18px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    &:active { opacity: 0.7; }
`;

const ModalDivider = styled.div`
    height: 1px;
    background: #f0f0f0;
    flex-shrink: 0;
`;

const ModalBody = styled.div`
    overflow-y: auto;
    flex: 1;
    padding: 16px 20px;
`;

const SlotGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const SlotChip = styled.div`
    padding: 8px 14px;
    border-radius: 10px;
    background: ${BRAND_GREEN_PRIMARY}15;
    color: ${BRAND_GREEN_PRIMARY};
    font-size: 14px;
    font-weight: 700;
`;

const ModalFooter = styled.div`
    padding: 12px 20px 16px;
    flex-shrink: 0;
`;

const EditBtn = styled.button`
    width: 100%;
    padding: 12px;
    border-radius: 12px;
    border: none;
    background: ${BRAND_GREEN_PRIMARY};
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    &:active { opacity: 0.85; }
`;

const EmptyModal = styled.div`
    text-align: center;
    padding: 24px 0 12px;
`;

// ── Componente ────────────────────────────────────────────────────────────────

const AgendaSummary = () => {
    const [user] = useUser();
    const history = useHistory();
    const today = startOfDay(new Date());

    const [modalDay, setModalDay] = useState(null);

    const slotsByDay = useMemo(() => {
        const availability = user.current?.extraData?.timeAvailability;
        return groupSlotsByDay(availability);
    }, [user.current]);

    const days = useMemo(() => {
        return Array.from({ length: 7 }, (_, i) => {
            const date = addDays(today, i);
            return {
                date,
                label: format(date, 'EEE', { locale: es }),
                num: format(date, 'd'),
                isToday: i === 0,
                dayKey: format(date, 'yyyy-MM-dd'),
            };
        });
    }, []);

    const handleDayClick = useCallback((day) => {
        const slots = slotsByDay[day.dayKey] || [];
        if (slots.length > 0) setModalDay(day);
    }, [slotsByDay]);

    const closeModal = useCallback(() => setModalDay(null), []);

    const goToTimetable = useCallback(() => {
        history.push('/horario');
        closeModal();
    }, []);

    const modalSlots = modalDay ? (slotsByDay[modalDay.dayKey] || []) : [];
    const modalTitle = modalDay
        ? (modalDay.isToday ? 'Hoy' : format(modalDay.date, "EEEE d 'de' MMMM", { locale: es }))
        : '';

    const hasAnySlots = days.some(d => (slotsByDay[d.dayKey] || []).length > 0);

    return (
        <>
            <Section>
                <SectionLabel>Mi semana</SectionLabel>
                <Card>
                    <WeekRow>
                        {days.map((day) => {
                            const slots = slotsByDay[day.dayKey] || [];
                            const hasSlots = slots.length > 0;
                            const isSelected = modalDay && modalDay.dayKey === day.dayKey;
                            return (
                                <DayBubble
                                    key={day.dayKey}
                                    isToday={day.isToday}
                                    hasSlots={hasSlots}
                                    isSelected={isSelected}
                                    onClick={() => handleDayClick(day)}
                                    aria-label={`${day.label} ${day.num} — ${slots.length} horarios`}
                                    title={hasSlots
                                        ? `${slots.length} horario${slots.length > 1 ? 's' : ''} disponible${slots.length > 1 ? 's' : ''}`
                                        : 'Sin disponibilidad'}
                                >
                                    <DayLabel isToday={day.isToday} hasSlots={hasSlots}>{day.label}</DayLabel>
                                    <DayNum isToday={day.isToday} hasSlots={hasSlots}>{day.num}</DayNum>
                                    <SlotBadge isToday={day.isToday}>
                                        {hasSlots ? `${slots.length}h` : ''}
                                    </SlotBadge>
                                </DayBubble>
                            );
                        })}
                    </WeekRow>
                    <EditHint onClick={goToTimetable}>
                        {hasAnySlots ? '✎ Editar disponibilidad' : '＋ Configurar mis horarios disponibles'}
                    </EditHint>
                </Card>
            </Section>

            {modalDay && (
                <Overlay onClick={closeModal}>
                    <ModalSheet onClick={e => e.stopPropagation()}>
                        <ModalHandle />
                        <ModalHeader>
                            <ModalTitle>{modalTitle}</ModalTitle>
                            <CloseBtn onClick={closeModal} aria-label="Cerrar">✕</CloseBtn>
                        </ModalHeader>
                        <ModalDivider />
                        <ModalBody>
                            {modalSlots.length === 0 ? (
                                <EmptyModal>
                                    <p style={{ fontSize: 36, margin: '0 0 10px' }}>📅</p>
                                    <p style={{ fontSize: 14, color: '#aaa', margin: 0 }}>Sin horarios disponibles este día</p>
                                </EmptyModal>
                            ) : (
                                <SlotGrid>
                                    {modalSlots.map((slot, i) => (
                                        <SlotChip key={i}>{format(slot, 'HH:mm')}</SlotChip>
                                    ))}
                                </SlotGrid>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <EditBtn onClick={goToTimetable}>
                                Editar disponibilidad de este día
                            </EditBtn>
                        </ModalFooter>
                    </ModalSheet>
                </Overlay>
            )}
        </>
    );
};

export default AgendaSummary;
